import React from 'react';
import { useOnboarding } from './OnboardingContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { 
  CheckCircle2, Circle, ArrowRight, Lightbulb, X
} from 'lucide-react';

/**
 * StepGuide - Shows contextual guidance for the current page
 * Displays the relevant step's substeps with completion tracking
 */
const StepGuide = ({ stepId, className = '' }) => {
  const {
    workflow,
    showChecklist,
    isSubstepCompleted,
    isStepCompleted,
    completeSubstep,
    getStepProgress
  } = useOnboarding();

  const [dismissed, setDismissed] = React.useState(false);
  const [showTips, setShowTips] = React.useState(true);

  // Find the step in workflow
  const step = workflow.find(s => s.id === stepId);
  
  if (!step || !showChecklist || dismissed || isStepCompleted(stepId)) {
    return null;
  }

  const { completed, total } = getStepProgress(stepId);
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

  const handleComplete = async (substepId) => {
    await completeSubstep(stepId, substepId);
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  return (
    <Card className={`bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 ${className}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-semibold text-emerald-900 text-sm">{step.title}</h4>
              <p className="text-xs text-emerald-700">{completed}/{total} tasks completed</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="h-6 w-6 p-0 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1.5 bg-emerald-200 rounded-full mb-3">
          <div 
            className="h-full bg-emerald-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Substeps */}
        <div className="space-y-2">
          {step.substeps?.map((substep, index) => {
            const isCompleted = isSubstepCompleted(stepId, substep.id);
            
            return (
              <div
                key={substep.id}
                className={`flex items-start gap-3 p-2 rounded-lg transition-all ${
                  isCompleted ? 'bg-emerald-100/50' : 'bg-white/60 hover:bg-white'
                }`}
              >
                <button
                  onClick={() => !isCompleted && handleComplete(substep.id)}
                  disabled={isCompleted}
                  className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all mt-0.5 ${
                    isCompleted
                      ? 'bg-emerald-500 border-emerald-500 text-white cursor-default'
                      : 'border-emerald-400 hover:border-emerald-600 hover:bg-emerald-50 cursor-pointer'
                  }`}
                >
                  {isCompleted && <CheckCircle2 className="w-3 h-3" />}
                </button>
                
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    isCompleted ? 'text-emerald-700 line-through' : 'text-gray-800'
                  }`}>
                    {substep.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {substep.description}
                  </p>
                </div>

                {!isCompleted && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleComplete(substep.id)}
                    className="h-7 px-2 text-xs text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100"
                  >
                    Done
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* All complete message */}
        {completed === total && total > 0 && (
          <div className="mt-3 p-2 bg-emerald-100 rounded-lg text-center">
            <p className="text-sm font-medium text-emerald-800">
              🎉 Great job! This section is complete.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StepGuide;
